import { useEffect, useState } from "react";
import { ModalShell } from "./ModalShell";

/**
 * One confirmation style for every destructive action, same as the phone app:
 * a title, one sentence, Cancel and a red verb ("Delete", "Leave", "Block" — never "OK").
 * `confirmAction()` can be awaited from anywhere; `<ConfirmHost />` (mounted once in the
 * app shell) renders the requests one at a time.
 */
export interface ConfirmOptions {
  title: string;
  body: string;
  action: string;
  /** Red action button (default). false for a non-destructive confirmation. */
  danger?: boolean;
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

let enqueue: ((p: Pending) => void) | null = null;

export function confirmAction(opts: ConfirmOptions): Promise<boolean> {
  return new Promise((resolve) => {
    // No host mounted (tests, very early screens): fall back to the browser dialog.
    if (!enqueue) {
      resolve(
        typeof window !== "undefined" && typeof window.confirm === "function"
          ? window.confirm(`${opts.title}\n\n${opts.body}`)
          : false,
      );
      return;
    }
    enqueue({ ...opts, resolve });
  });
}

export function confirmClearChat(isGroup: boolean): Promise<boolean> {
  return confirmAction({
    title: "Clear this chat?",
    body: `All messages and media in this chat are removed from this device. ${isGroup ? "Other members keep their copy." : "Your contact keeps their copy."}`,
    action: "Clear",
  });
}

export function ConfirmHost() {
  const [queue, setQueue] = useState<Pending[]>([]);
  useEffect(() => {
    enqueue = (p) => setQueue((q) => [...q, p]);
    return () => {
      enqueue = null;
    };
  }, []);
  const current = queue[0];
  if (!current) return null;
  const finish = (ok: boolean) => {
    current.resolve(ok);
    setQueue((q) => q.slice(1));
  };
  return (
    <ModalShell
      title={current.title}
      ariaLabel={current.title}
      onClose={() => finish(false)}
      size="sm"
      className="confirm-card"
      footer={
        <div className="actions-row">
          <button type="button" className="btn secondary" onClick={() => finish(false)}>
            Cancel
          </button>
          <button
            type="button"
            className={current.danger === false ? "btn" : "btn danger"}
            autoFocus
            onClick={() => finish(true)}
          >
            {current.action}
          </button>
        </div>
      }
    >
      <p className="confirm-body">{current.body}</p>
    </ModalShell>
  );
}
